import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Field,
  Radio,
  RadioGroup,
  Subtitle1,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';

import { useDocumentTitle, useSearchParams } from '@shared/hooks';
import { TItem, useFolder } from '@modules/items';

import { ReviewBasisPicker } from '../components/setup/ReviewBasisPicker';
import { ScopeField } from '../components/setup/ScopeField';
import { useReviewStyles } from '../components/shared';
import { REVIEW_TYPES } from '../constants';
import { getReviewPath, getReviewsPath, resolveScenario } from '../helpers';
import { ReviewPageScaffold } from '../layouts/ReviewPageScaffold';
import { useReviewStore } from '../state/ReviewStore';
import { TReviewTypeId } from '../types';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
    maxWidth: '760px',
  },
  context: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
});

const ReviewSetupContent = () => {
  const classes = useStyles();
  const reviewClasses = useReviewStyles();
  const navigate = useNavigate();
  const { searchParams } = useSearchParams();
  const productId = searchParams.get('productId') ?? undefined;

  const { config, reviewBases, startReview } = useReviewStore();
  const { data: productFolder } = useFolder(productId, { enabled: Boolean(productId) });

  const [scopeItems, setScopeItems] = useState<TItem[]>([]);
  const [reviewType, setReviewType] = useState<TReviewTypeId>('full');
  const [basisIds, setBasisIds] = useState<string[]>([]);
  const [instructions, setInstructions] = useState('');
  const [uploadedName, setUploadedName] = useState<string | undefined>(undefined);
  const fileInput = useRef<HTMLInputElement>(null);

  // The product line follows the basis selection, so the context shown is the one
  // the review will run against.
  const scenario = useMemo(() => resolveScenario(config, basisIds, productId), [config, basisIds, productId]);
  const productName = (productFolder as { name?: string } | undefined)?.name ?? scenario.context?.productName;
  const productContext = scenario.context?.productContext;

  const canStart = scopeItems.length > 0 && basisIds.length > 0;

  const handleStart = () => {
    const reviewId = startReview({
      reviewType,
      reviewBasisIds: basisIds,
      scopeItems: scopeItems.map((item) => ({ _id: item._id, name: item.name })),
      instructions: instructions.trim() || undefined,
      productId,
      productName,
    });
    navigate(getReviewPath(reviewId, productId));
  };

  return (
    <>
      <Subtitle1>New review</Subtitle1>

      <div className={`${reviewClasses.surface} ${classes.form}`}>
        {productId && productName ? (
          <div className={classes.context}>
            <Body1Strong>{productName}</Body1Strong>
            {productContext ? <Caption1 className={classes.muted}>{productContext}</Caption1> : null}
          </div>
        ) : null}

        <Field label="Scope" required>
          <ScopeField folderId={productId} items={scopeItems} onChange={setScopeItems} />
        </Field>

        <Field label="Review type">
          <RadioGroup value={reviewType} onChange={(_, data) => setReviewType(data.value as TReviewTypeId)}>
            {REVIEW_TYPES.map((type) => (
              <Radio key={type.id} value={type.id} label={type.label} />
            ))}
          </RadioGroup>
        </Field>

        <Field label="Review basis" required>
          <>
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                setUploadedName(file.name);
                setBasisIds((previous) =>
                  previous.includes('uploaded-pdf') ? previous : [...previous, 'uploaded-pdf']
                );
              }}
            />
            <ReviewBasisPicker
              bases={reviewBases}
              selectedIds={basisIds}
              onChange={setBasisIds}
              uploadedName={uploadedName}
              onUpload={() => fileInput.current?.click()}
            />
          </>
        </Field>

        <Field label="Instructions" hint="Optional">
          <Textarea
            value={instructions}
            onChange={(_, data) => setInstructions(data.value)}
            placeholder="Anything the review should pay attention to"
            resize="vertical"
          />
        </Field>

        <div className="flex items-center gap-3">
          <Button appearance="primary" disabled={!canStart} onClick={handleStart}>
            Start review
          </Button>
          <Button appearance="subtle" onClick={() => navigate(getReviewsPath(productId))}>
            Cancel
          </Button>
          {!canStart ? <Body1 className={classes.muted}>Select a scope and at least one review basis</Body1> : null}
        </div>
      </div>
    </>
  );
};

const ReviewSetupPage = () => {
  useDocumentTitle('New review');
  return (
    <ReviewPageScaffold breadcrumbTail={[{ name: 'New review' }]}>
      <ReviewSetupContent />
    </ReviewPageScaffold>
  );
};

export default ReviewSetupPage;
